/**
 * /medical-consultation — Google Ads SEM landing page.
 *
 * This is a paid-search only surface. Rules enforced here:
 *   • Excluded from indexing: <meta name="robots" content="noindex, nofollow"/>
 *   • Excluded from the sitemap (see backend/server.py sitemap generator).
 *   • Not linked from primary nav / footer / any content page (crawl isolation).
 *   • Reuses the canonical <ConsultationRequestForm> — no duplicated business
 *     logic. Submissions flow through the same Resend + /thank-you pipeline.
 *   • form_name="google_ads_consultation" so downstream analytics can distinguish
 *     SEM traffic from primary /request-consultation traffic.
 *   • No unsupported medical claims. Every efficacy-adjacent statement is
 *     conditioned on provider evaluation and eligibility.
 */
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck, CheckCircle2, Award, HeartPulse, Sparkles, Activity,
  Leaf, Users, Phone, Mail, ArrowDown,
} from "lucide-react";
import ConsultationRequestForm from "../components/ConsultationRequestForm";
import { trackEvent } from "../lib/analytics";

const OFFERINGS = [
  {
    icon: HeartPulse,
    title: "Hormone Optimization",
    body: "Provider evaluation for hormone-related concerns and considerations for age-appropriate therapy.",
  },
  {
    icon: Activity,
    title: "Weight Management",
    body: "Individualized assessment for GLP-1 and metabolic-support options when clinically appropriate.",
  },
  {
    icon: Sparkles,
    title: "Recovery & Performance",
    body: "Evaluation-based discussion of peptide options that may support recovery goals under medical guidance.",
  },
  {
    icon: Leaf,
    title: "Healthy Aging",
    body: "Provider-led discussion of longevity-focused options based on your medical history and objectives.",
  },
  {
    icon: Award,
    title: "Hair & Skin",
    body: "Assessment for aesthetic-focused peptide options where medically appropriate.",
  },
  {
    icon: Users,
    title: "General Wellness",
    body: "Not sure where to start? Our providers help identify whether peptide therapy fits your goals.",
  },
];

const STEPS = [
  { n: 1, title: "Request a Consultation", body: "Submit the short form. A member of our care team will follow up to schedule your visit." },
  { n: 2, title: "Speak With a Licensed Provider", body: "Meet with a licensed VidaVital Medical clinician — virtually or in-person, depending on your state and preference." },
  { n: 3, title: "Receive an Individualized Evaluation", body: "Your provider reviews your goals and medical history to determine whether peptide therapy may be appropriate." },
  { n: 4, title: "Personalized Plan (If Appropriate)", body: "If clinically appropriate, receive a personalized plan and coordinated follow-up. If not, we'll be candid about that too." },
];

const FAQ = [
  {
    q: "What happens during the consultation?",
    a: "Your provider reviews your health history and goals, discusses relevant options, answers questions, and — where clinically appropriate — outlines a personalized treatment plan. Consultations are educational, not sales calls.",
  },
  {
    q: "Who may be a candidate?",
    a: "Eligibility depends on your medical history, provider assessment, and applicable laws. Not every visitor qualifies for treatment. Your provider will explain what fits your situation and what does not.",
  },
  {
    q: "Do I need laboratory testing?",
    a: "Sometimes. Your provider may recommend baseline labs to inform treatment decisions. Testing requirements depend on the therapies being considered and your clinical picture.",
  },
  {
    q: "Can appointments be virtual or in person?",
    a: "Both, where legally appropriate. We offer telehealth visits in states where our providers are licensed as well as in-person visits at our clinic locations.",
  },
  {
    q: "How long does the consultation take?",
    a: "Initial consultations typically run 30 to 45 minutes. Follow-ups are usually shorter unless there is a lot to discuss.",
  },
];

const TRUST_ROW = [
  "Licensed Medical Providers",
  "Personalized Treatment Plans",
  "Evidence-Based Care",
  "HIPAA-Conscious Communication",
  "Ongoing Clinical Support",
];

export default function MedicalConsultation() {
  // Landing-page-view analytics ping. Fire-and-forget; failures never break UX.
  useEffect(() => {
    try {
      trackEvent("landing_page_view", { page: "/medical-consultation", source: "google-ads" });
    } catch (_) { /* analytics never blocks the UI */ }
  }, []);

  const scrollToForm = () => {
    try { trackEvent("consultation_click", { location: "sem-landing-hero" }); } catch (_) { /* analytics never blocks the UI */ }
    document.getElementById("consultation-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="medical-consultation-page">
      <Helmet>
        <title>Medical Consultation for Peptide Therapy | MedicalPeptides.ai</title>
        <meta
          name="description"
          content="Request a consultation with a licensed medical provider to discuss whether peptide therapy is appropriate for your goals. Personalized plans if clinically appropriate."
        />
        {/* Hard-block indexing for the SEM landing page. Regardless of the
            environment-aware SEO utility used elsewhere on the site, this page
            must never be crawled/indexed because it exists solely for paid ads
            and must not compete with organic content for query intent. */}
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        {/* Explicit "no canonical" — canonicalizing to a live URL would give
            crawlers a hint to still find and rank the SEM copy. Better to be
            silent about canonicals here. */}
      </Helmet>

      {/* --------------------------------------------------------------- HERO */}
      <section
        data-testid="mc-hero"
        className="bg-[color:var(--vv-ink)] text-white"
      >
        <div className="vv-container py-14 md:py-20 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold)] font-semibold">
              <ShieldCheck size={14} /> Licensed Medical Providers
            </div>
            <h1
              data-testid="mc-hero-headline"
              className="font-display mt-4 text-3xl md:text-5xl lg:text-6xl leading-[1.05]"
            >
              Personalized Peptide Therapy Starts With a Medical Consultation.
            </h1>
            <p className="mt-6 text-stone-200 md:text-lg leading-relaxed max-w-2xl">
              Meet with a licensed medical provider to discuss your health goals, review whether peptide therapy may be appropriate for your situation, and receive a personalized treatment plan if clinically appropriate.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="mc-hero-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--vv-gold-deep)] text-white font-semibold px-7 py-3.5 text-sm uppercase tracking-widest hover:opacity-95 transition"
              >
                Request Your Consultation
                <ArrowDown size={16} />
              </button>
              <a
                href="tel:+18887738677"
                data-testid="mc-hero-phone"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-500 text-white font-semibold px-6 py-3.5 text-sm uppercase tracking-widest hover:bg-white/5 transition"
              >
                <Phone size={16} /> (888) 773-8677
              </a>
            </div>
            <ul
              data-testid="mc-trust-strip"
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm text-stone-200"
            >
              {TRUST_ROW.map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[color:var(--vv-gold)]" /> {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Compact CTA card visible above the fold on desktop */}
          <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 md:p-7 shadow-2xl">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold)] font-semibold">Book Your Consultation</div>
            <div className="mt-2 text-white text-2xl font-display leading-snug">Start with a licensed provider.</div>
            <p className="mt-3 text-stone-300 text-sm leading-relaxed">
              A member of our care team will follow up shortly after you submit. No obligation, no automated scheduling — just a real conversation.
            </p>
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="mc-hero-cta-alt"
              className="mt-5 w-full inline-flex items-center justify-center rounded-full bg-[color:var(--vv-gold-deep)] text-white font-semibold px-6 py-3 text-sm uppercase tracking-widest hover:opacity-95"
            >
              Request Consultation
            </button>
            <div className="mt-4 text-xs text-stone-400">
              Evaluation required. Treatment recommendations are individualized.
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- WHAT WE OFFER */}
      <section data-testid="mc-offerings" className="vv-container py-14 md:py-20">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold-deep)] font-semibold">What We Offer</div>
        <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight max-w-2xl">
          Provider-led evaluations across the areas patients ask us about most.
        </h2>
        <p className="mt-3 text-stone-600 max-w-2xl">
          These are areas your provider may discuss with you. Not every visitor is a candidate for every therapy — eligibility depends on your medical history and clinical evaluation.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {OFFERINGS.map((o) => {
            const Icon = o.icon;
            return (
              <div
                key={o.title}
                data-testid={`mc-offering-${o.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-xl border border-stone-200 bg-white p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-full bg-[color:var(--vv-gold-deep)]/10 text-[color:var(--vv-gold-deep)] flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <div className="mt-4 font-semibold text-stone-900">{o.title}</div>
                <div className="mt-2 text-sm text-stone-600 leading-relaxed">{o.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------------------------------------------------- HOW IT WORKS */}
      <section data-testid="mc-how-it-works" className="bg-stone-50 border-y border-stone-200">
        <div className="vv-container py-14 md:py-20">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold-deep)] font-semibold">How It Works</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight max-w-2xl">
            Four steps from request to personalized care.
          </h2>
          <ol className="mt-10 grid md:grid-cols-4 gap-4">
            {STEPS.map((s) => (
              <li
                key={s.n}
                data-testid={`mc-step-${s.n}`}
                className="relative rounded-xl border border-stone-200 bg-white p-6"
              >
                <div className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">Step {s.n}</div>
                <div className="mt-2 font-semibold text-stone-900">{s.title}</div>
                <div className="mt-2 text-sm text-stone-600 leading-relaxed">{s.body}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ------------------------------------------------------------- FORM */}
      <section
        id="consultation-form"
        data-testid="mc-form-section"
        className="vv-container py-14 md:py-20 grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-16"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold-deep)] font-semibold">Request Consultation</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
            Talk with a licensed provider.
          </h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            Share a few details and a member of our team will follow up to schedule your consultation. Please do not include medical history or protected health information in this form.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <div className="flex items-center gap-2 text-stone-700">
              <Mail size={16} className="text-[color:var(--vv-gold-deep)]" />
              <a href="mailto:info@vidavitalmedical.com" className="underline hover:text-[color:var(--vv-gold-deep)]">info@vidavitalmedical.com</a>
            </div>
            <div className="flex items-center gap-2 text-stone-700">
              <Phone size={16} className="text-[color:var(--vv-gold-deep)]" />
              <a href="tel:+18887738677" className="underline hover:text-[color:var(--vv-gold-deep)]">(888) 773-8677</a>
            </div>
            <div className="text-xs text-stone-500 pt-2">
              For medical emergencies, call 911 or your local emergency services.
            </div>
          </div>
        </div>

        <ConsultationRequestForm
          formName="google_ads_consultation"
          sourceLabel="/medical-consultation"
          testIdPrefix="mc"
          showEmergencyBlurb={false}
          submitLabel="Request Your Consultation"
        />
      </section>

      {/* --------------------------------------------------------------- FAQ */}
      <section data-testid="mc-faq" className="bg-stone-50 border-t border-stone-200">
        <div className="vv-container py-14 md:py-20 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--vv-gold-deep)] font-semibold">Frequently Asked</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
            Answers before your consultation.
          </h2>
          <div className="mt-8 divide-y divide-stone-200 border-y border-stone-200">
            {FAQ.map((item, i) => (
              <details key={item.q} data-testid={`mc-faq-${i}`} className="group py-4">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-semibold text-stone-900">{item.q}</span>
                  <span className="text-[color:var(--vv-gold-deep)] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- COMPLIANCE FOOT */}
      <section className="vv-container py-10 text-xs text-stone-500 leading-relaxed">
        <p>
          <strong>Important:</strong> Treatment recommendations are individualized. Every patient is evaluated by a licensed medical provider. Eligibility depends on medical history, provider assessment, and applicable laws. Information on this page is educational and does not replace personalized medical advice, diagnosis, or treatment. This page does not create a provider-patient relationship. Peptides discussed are prescribed only when clinically appropriate and in accordance with applicable laws.
        </p>
      </section>
    </div>
  );
}
